// React Query обвивките за доставките, добавени с редизайна. Инвалидацията
// минава през `invalidateAll` (useInventory.ts) — единственото място.
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { getDelivery, type Delivery } from "../api/inventory";
import {
  annulDelivery,
  closeNotesWithoutInvoice,
  detachNotesFromInvoice,
  getDeliveriesReport,
  getPayableDeliveries,
  getUninvoicedNotes,
  payDeliveries,
  reopenNotesForInvoice,
  unpayDeliveries,
  type CloseWithoutInvoicePayload,
  type DeliveriesReportParams,
  type PayDeliveriesPayload,
} from "../api/inventoryDeliveries";
import { useIsEdgeLocal } from "./useEdgeMode";
import { invalidateAll } from "./useInventory";

export const useDeliveriesReport = (params: DeliveriesReportParams | null) =>
  useQuery({
    queryKey: ["inventory", "deliveries", "report", params],
    queryFn: () => getDeliveriesReport(params!),
    enabled: !!params,
  });

export const useUninvoicedNotes = (supplierId?: number | null, enabled = true) =>
  useQuery({
    queryKey: ["inventory", "uninvoiced-notes", supplierId ?? null],
    queryFn: () => getUninvoicedNotes(supplierId),
    enabled,
    retry: false,
  });

// Стоковите, които фактурата обобщава — редовете им се показват в документа
// на фактурата (собствени редове тя може и да няма). Ключът е под
// ["inventory", "deliveries"], за да го обновява `invalidateAll`.
export const useLinkedNotes = (noteIds: number[]) =>
  useQueries({
    queries: noteIds.map((id) => ({
      queryKey: ["inventory", "deliveries", "one", id],
      queryFn: () => getDelivery(id),
      staleTime: 30_000,
    })),
    combine: (results) => ({
      notes: results.map((result) => result.data).filter((note): note is Delivery => !!note),
      loading: results.some((result) => result.isLoading),
    }),
  });

// Неплатените доставки (без период). До BE с плащане → 404 → празен списък,
// а екранът казва, че плащането иска новата версия.
export const usePayableDeliveries = () => {
  const isLocal = useIsEdgeLocal();
  return useQuery({
    queryKey: ["inventory", "payable"],
    queryFn: getPayableDeliveries,
    enabled: !isLocal,
    retry: false,
  });
};

export const usePayDeliveries = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PayDeliveriesPayload) => payDeliveries(payload),
    onSuccess: () => invalidateAll(qc),
  });
};

export const useUnpayDeliveries = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => unpayDeliveries(ids),
    onSuccess: () => invalidateAll(qc),
  });
};

export const useDetachNotesFromInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => detachNotesFromInvoice(ids),
    onSuccess: () => invalidateAll(qc),
  });
};

export const useCloseNotesWithoutInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CloseWithoutInvoicePayload) => closeNotesWithoutInvoice(payload),
    onSuccess: () => invalidateAll(qc),
  });
};

export const useReopenNotesForInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => reopenNotesForInvoice(ids),
    onSuccess: () => invalidateAll(qc),
  });
};

export const useAnnulDelivery = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string | null }) =>
      annulDelivery(id, reason),
    onSuccess: () => invalidateAll(qc),
  });
};
